/**
 * The line a stroke is written along, as a path and as a distance.
 *
 * Make Me a Hanzi gives every stroke a median, which is the route a hand takes
 * writing it, as points on the same 1024 by 1024 grid the outlines use.
 * `bishun-drawing.ts` draws a brush along it and `bishun-playback.ts` times the
 * brush by how far it has to travel.
 */

import type { Median } from "./bishun-drawing.js";

/** A median as the path a brush follows along it. */
export function medianPath(median: Median): string {
  const points = median.map(([x, y]) => `${String(x)} ${String(y)}`);

  /*
   * A median of one point is drawn as a line to itself, so that the round cap
   * renders it. `M x y` on its own draws nothing at all, which would lose the
   * stroke rather than showing it as the dot it is.
   */
  const first = points[0] ?? "0 0";

  return points.length > 1
    ? `M ${points.join(" L ")}`
    : `M ${first} L ${first}`;
}

/** How far the brush travels along a median. */
export function medianLength(median: Median): number {
  return median.reduce((total, point, at) => {
    const previous = median[at - 1];

    if (previous === undefined) {
      return total;
    }

    return total + Math.hypot(point[0] - previous[0], point[1] - previous[1]);
  }, 0);
}
